const TIERS = [{ period: 0.1, span: 1200 }, { period: 1, span: 7200 }, { period: 10, span: 28800 }]

function createTier(nSignals, { period, span }) {
  const cap = Math.ceil(span / period) + 2
  const ts = new Float64Array(cap)
  const min = Array.from({ length: nSignals }, () => new Float64Array(cap).fill(Infinity))
  const max = Array.from({ length: nSignals }, () => new Float64Array(cap).fill(-Infinity))
  const last = Array.from({ length: nSignals }, () => new Float64Array(cap))
  let head = 0, count = 0, bucketIdx = null
  function slot() { return (head - 1 + cap) % cap }
  return {
    period, span,
    push(t, s) {
      // Integer bucket index; the epsilon absorbs float error (0.3 / 0.1 = 2.999...).
      const idx = Math.floor(t / period + 1e-9)
      if (bucketIdx === null || idx > bucketIdx) {
        bucketIdx = idx
        const i = head; head = (head + 1) % cap; if (count < cap) count++
        ts[i] = idx * period
        for (let k = 0; k < nSignals; k++) { min[k][i] = s[k]; max[k][i] = s[k]; last[k][i] = s[k] }
      } else {
        const i = slot()
        for (let k = 0; k < nSignals; k++) { if (s[k] < min[k][i]) min[k][i] = s[k]; if (s[k] > max[k][i]) max[k][i] = s[k]; last[k][i] = s[k] }
      }
    },
    /**
     * One pass over the ring for every requested signal. The start slot is found by binary search (ts ascend in ring
     * order). When more than maxPoints rows match, min/max/last are decimated into maxPoints buckets; the bucket of a
     * row depends only on its timestamp, so it is computed once and shared by all signals.
     */
    readMany(indices, from, to, maxPoints) {
      const at = (j) => ts[(head - count + j + cap) % cap]
      let lo = 0, hi = count
      while (lo < hi) { const m = (lo + hi) >> 1; if (at(m) < from) lo = m + 1; else hi = m }
      const start = lo
      hi = count
      while (lo < hi) { const m = (lo + hi) >> 1; if (at(m) <= to) lo = m + 1; else hi = m }
      const n = lo - start
      const slots = new Int32Array(n)
      for (let j = 0; j < n; j++) slots[j] = (head - count + start + j + cap) % cap
      if (n <= maxPoints) {
        const rowTs = new Float64Array(n)
        for (let j = 0; j < n; j++) rowTs[j] = ts[slots[j]]
        return indices.map((k) => {
          const mn = min[k], mx = max[k], ls = last[k]
          const o = { ts: rowTs.slice(), min: new Float64Array(n), max: new Float64Array(n), last: new Float64Array(n) }
          for (let j = 0; j < n; j++) { const i = slots[j]; o.min[j] = mn[i]; o.max[j] = mx[i]; o.last[j] = ls[i] }
          return o
        })
      }
      // Decimate. The epsilon absorbs float error on exact bucket boundaries (0.7 / 0.1 = 6.999...).
      const width = (to - from) / Math.max(1, maxPoints)
      const outIdx = new Int32Array(n)
      const bucketT = new Float64Array(maxPoints)
      let nOut = 0, prev = -1
      for (let j = 0; j < n; j++) {
        const b = Math.min(maxPoints - 1, Math.floor((ts[slots[j]] - from) / width + 1e-9))
        if (b !== prev) { bucketT[nOut++] = from + b * width; prev = b }
        outIdx[j] = nOut - 1
      }
      const outTs = bucketT.slice(0, nOut)
      return indices.map((k) => {
        const mn = min[k], mx = max[k], ls = last[k]
        const o = { ts: outTs.slice(), min: new Float64Array(nOut).fill(Infinity), max: new Float64Array(nOut).fill(-Infinity), last: new Float64Array(nOut) }
        for (let j = 0; j < n; j++) {
          const i = slots[j], q = outIdx[j]
          if (mn[i] < o.min[q]) o.min[q] = mn[i]
          if (mx[i] > o.max[q]) o.max[q] = mx[i]
          o.last[q] = ls[i]
        }
        return o
      })
    },
    full() { return count === cap },
    oldest() { return count ? ts[(head - count + cap) % cap] : null },
    newest() { return count ? ts[slot()] : null },
  }
}

export function createHistory(nSignals) {
  const tiers = TIERS.map((t) => createTier(nSignals, t))
  return {
    push(t, s) { for (const tier of tiers) tier.push(t, s) },
    /**
     * The page's backfill: a sample older than a tier keeps by `endT` (the time the backfill ends at) is not written to
     * that tier, since it would be overwritten before anyone reads it. Each tier still receives more than its ring holds
     * (span + 4 periods), so once the backfill ends every ring holds exactly what push() would have left in it.
     */
    pushBackfill(t, s, endT) { for (const tier of tiers) if (endT - t <= tier.span + 4 * tier.period) tier.push(t, s) },
    tierFor(span) { return span <= 1200 ? 0 : span <= 7200 ? 1 : 2 },
    range() { return { from: tiers[2].oldest(), to: tiers[0].newest() } },
    /** Finest tier (from tierFor(span) upward) whose oldest bucket reaches `from`; none reaches (all wrapped) -> coarsest. */
    pickTier(from, to) {
      for (let i = this.tierFor(to - from); i < 3; i++) {
        const o = tiers[i].oldest()
        // A tier that has not wrapped holds all history, so young history is served at this tier's resolution.
        if (o !== null && (o <= from + tiers[i].period || !tiers[i].full())) return i
      }
      return 2
    },
    queryMany(indices, from, to, maxPoints) { return tiers[this.pickTier(from, to)].readMany(indices, from, to, maxPoints) },
    query(k, from, to, maxPoints) { return this.queryMany([k], from, to, maxPoints)[0] },
  }
}
